import React, { useState, useEffect } from "react";
import { Container, Typography, Paper, Select, MenuItem, FormControl, InputLabel,  FormControlLabel, Box, Button, Checkbox, Stack, Table, TableBody, TableCell, TableRow } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import BudgetInput from "./BudgetInput";
import ExpenseTable from "./ExpenseTable";
import { currencies } from "../currencies"; // Import currencies
import { calculateBudgetTotals } from "./budgetUtils";
import { Helmet } from "react-helmet";
import '../App.css'; // Import the CSS file for styles

function BudgetApp() {
  const defaultBudget = '2500';
  const defaultExpenses = [
    { label: "Rent", amount: '1200' },
    { label: "Groceries", amount: '300' },
    { label: "Transport", amount: '150' },
    { label: "Utilities", amount: '180' },
  ];
  const [budget, setBudget] = useState(defaultBudget);
  const [expenses, setExpenses] = useState(defaultExpenses);
  const [currency, setCurrency] = useState("GBP"); // Set default currency to GBP
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [suppressNextSave, setSuppressNextSave] = useState(false);
  const [saveToLocal, setSaveToLocal] = useState(() => {
    const storedPreference = localStorage.getItem("saveBudgetData");
    return storedPreference !== null ? JSON.parse(storedPreference) : true;
  });


  // Update expense function
  const updateExpense = (index, key, value) => {
    const updatedExpenses = [...expenses];
    updatedExpenses[index][key] = value;
    setExpenses(updatedExpenses);
  };

  // Add and remove rows
  const addRow = () => setExpenses([...expenses, { label: "", amount: '0' }]);
  const removeRow = (index) => setExpenses(expenses.filter((_, i) => i !== index));

  const { totalExpenses, budgetValue: numericBudget, remaining } = calculateBudgetTotals(budget, expenses);

  const clearData = () => {
    localStorage.removeItem("budget");
    localStorage.removeItem("expenses");
    localStorage.removeItem("currency");
    setSuppressNextSave(true);
    setBudget('0');
    setExpenses([{ label: "", amount: '0' }]);
    setCurrency("GBP");
  };

  // Save to local storage
  useEffect(() => {
    localStorage.setItem("saveBudgetData", JSON.stringify(saveToLocal));
    if (suppressNextSave) {
      setSuppressNextSave(false);
      return;
    }
    if (!saveToLocal || isInitialLoad) {
      return;
    }
    localStorage.setItem("budget", budget === '' ? '0' : budget);
    localStorage.setItem("expenses", JSON.stringify(expenses));
    localStorage.setItem("currency", currency); // Save the current currency
    console.log("Saving to local storage:", budget, expenses, currency); // Log the saving process
  }, [budget, expenses, currency, isInitialLoad, saveToLocal, suppressNextSave]);

  // Load from local storage on app initialization
  useEffect(() => {
    if (!saveToLocal) {
      setIsInitialLoad(false);
      return;
    }
    const savedBudget = localStorage.getItem("budget");
    const savedExpenses = localStorage.getItem("expenses");
    const savedCurrency = localStorage.getItem("currency");

    if (savedBudget !== null) {
      setBudget(savedBudget);
    } else {
      setBudget(defaultBudget);
    }
    if (savedExpenses !== null) {
      try {
        const parsed = JSON.parse(savedExpenses);
        const normalized = Array.isArray(parsed)
          ? parsed.map((exp) => ({
              label: exp.label || "",
              amount:
                exp.amount === undefined || exp.amount === null
                  ? '0'
                  : typeof exp.amount === 'number'
                  ? String(exp.amount)
                  : String(exp.amount),
            }))
          : [{ label: "", amount: '0' }];
        setExpenses(normalized);
      } catch (_) {
        setExpenses(defaultExpenses);
      }
    } else {
      setExpenses(defaultExpenses);
    }
    if (savedCurrency) {
      setCurrency(savedCurrency);
    }

    console.log("Loading from local storage:", { savedBudget, savedExpenses, savedCurrency });
    setIsInitialLoad(false); // Set this after loading
  }, [saveToLocal]);

// Export function
const exportToCSV = () => {
    const header = ["Item", "Amount"]; // Renamed "Label" to "Item"
    const rows = expenses.map(exp => [exp.label, `${currencies[currency].symbol}${exp.amount}`]);
  
    // Include the budget and remaining total in the CSV
    const budgetRow = ["Budget", `${currencies[currency].symbol}${numericBudget.toFixed(2)}`]; // Changed "Initial Budget" to "Budget"
    const totalRow = ["Remaining", `${currencies[currency].symbol}${remaining.toFixed(2)}`];
  
    const csvContent = "data:text/csv;charset=utf-8," 
                      + header.join(",") + "\n"
                      + budgetRow.join(",") + "\n" // Add budget
                      + rows.map(e => e.join(",")).join("\n") + "\n" 
                      + totalRow.join(",");
  
    // Create a date string for the filename
    const date = new Date();
    const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}_${date.getHours().toString().padStart(2, '0')}-${date.getMinutes().toString().padStart(2, '0')}-${date.getSeconds().toString().padStart(2, '0')}`;
  
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `budget_data_${formattedDate}.csv`); // Set the filename with date and time
    document.body.appendChild(link); // Required for Firefox
    link.click();
    document.body.removeChild(link); // Clean up
  };
  

  return (
    <>
      <Helmet>
        <title>Budget Calculator | Talk About Finance</title>
        <meta
          name="description"
          content="Track your spending, log expenses, and see what is left in real time with a free budget calculator."
        />
        <link rel="canonical" href="https://talkabout.finance/apps/budget-calculator" />
      </Helmet>

      <Container maxWidth="md" sx={{ pb: 8 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mt: 4,
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => theme.shadows[1],
          background: (theme) =>
            theme.palette.mode === 'light'
              ? 'linear-gradient(135deg, #f7f9fc 0%, #ffffff 45%, #f7f9fc 100%)'
              : 'linear-gradient(135deg, #1d1f24 0%, #17181c 45%, #1d1f24 100%)',
        }}
      >
        <Stack spacing={1.5}>
          <Box>
            <Button variant="outlined" color="primary" component={RouterLink} to="/apps">
              Back to apps
            </Button>
          </Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
            Planner
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Budget
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 780 }}>
            Track your budget, log expenses, and see what is left in real time. Keep your entries on this
            device if you like, and export them as a spreadsheet when you are done.
          </Typography>
        </Stack>
      </Paper>

      <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: (theme) => theme.shadows[2] }}>
        <Stack spacing={2}>
          <Box sx={{ maxWidth: 420 }}>
            <BudgetInput budget={budget} setBudget={setBudget} currency={currency} />
          </Box>

          <ExpenseTable
            expenses={expenses}
            updateExpense={updateExpense}
            addRow={addRow}
            removeRow={removeRow}
            currency={currency}
          />

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              borderColor: 'divider',
              boxShadow: (theme) => theme.shadows[0],
            }}
          >
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ width: '40%' }}>Budget</TableCell>
                  <TableCell align="right">{currencies[currency].symbol}{numericBudget.toFixed(2)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Total Expenses</TableCell>
                  <TableCell align="right">{currencies[currency].symbol}{totalExpenses.toFixed(2)}</TableCell>
                </TableRow>
                <TableRow selected={remaining < 0}>
                  <TableCell sx={{ fontWeight: 700 }}>Remaining</TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, color: remaining < 0 ? 'error.main' : 'success.main' }}
                  >
                    {currencies[currency].symbol}{remaining.toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>

          <FormControl fullWidth>
            <InputLabel id="currency-label" shrink>Currency</InputLabel>
            <Select
              labelId="currency-label"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              variant="outlined"
              displayEmpty
              label="Currency"
            >
              {Object.entries(currencies).map(([code, { symbol }]) => (
                <MenuItem key={code} value={code}>
                  {symbol} {code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" alignItems="center" gap={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={saveToLocal}
                  onChange={(e) => setSaveToLocal(e.target.checked)}
                />
              }
              label="Save these entries for next time"
            />
            <Button variant="outlined" color="secondary" onClick={clearData}>
              Clear data
            </Button>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={exportToCSV}
            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          >
            Export as Spreadsheet
          </Button>
        </Stack>
      </Paper>
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          About these calculations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          These figures are illustrative and based on the assumptions shown. They may not reflect the terms of specific financial products or your individual circumstances. You should verify results independently and, where appropriate, seek advice from a qualified professional.
        </Typography>
      </Box>
      </Container>
    </>
  );
}

export default BudgetApp;
